import { displayName } from '@lineage/core';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../lib/api';
import { withCode } from '../../lib/errorCode';
import { keys, useMe, useMembers, useTree } from '../../lib/queries';
import { InviteDialog } from '../person/InviteDialog';
import { ImportExport } from '../import-export/ImportExport';
import { formatDay, useLang, useT } from '../../i18n';
import { BackLink } from '../../components/BackLink';

interface InviteRow { id: string; email: string | null; role: string; personId: string | null; maxUses: number; uses: number; expiresAt: number }

export function Members({ treeId }: { treeId: string }) {
  const t = useT();
  const tm = t.members;
  const lang = useLang();
  const tree = useTree(treeId);
  const members = useMembers(treeId);
  const qc = useQueryClient();
  const nav = useNavigate();
  const myId = useMe().data?.user.id;
  const toast = useToast((s) => s.show);
  const snap = tree.data;
  const role = snap?.me.role;
  const isOwner = role === 'owner';
  const invites = useQuery({
    queryKey: keys.invites(treeId),
    queryFn: () => api<{ invites: InviteRow[] }>('GET', `/trees/${treeId}/invites`).then((r) => r.invites),
    enabled: isOwner,
  });
  const [name, setName] = useState('');
  const [inviting, setInviting] = useState(false);
  const onErr = (e: unknown) => toast(e instanceof ApiError ? e.message : withCode(t.common.somethingWrong, e));
  const reload = () => {
    void qc.invalidateQueries({ queryKey: keys.members(treeId) });
    void qc.invalidateQueries({ queryKey: keys.tree(treeId) });
    void qc.invalidateQueries({ queryKey: keys.invites(treeId) });
  };
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: object }) => api('PATCH', `/trees/${treeId}/members/${id}`, body), onSuccess: reload, onError: onErr });
  const kick = useMutation({
    mutationFn: (id: string) => api('DELETE', `/trees/${treeId}/members/${id}`),
    onSuccess: (_r, id) => (id === myId ? void nav({ to: '/trees' }) : reload()),
    onError: onErr,
  });
  const resolve = useMutation({ mutationFn: ({ id, approve }: { id: string; approve: boolean }) => api('POST', `/trees/${treeId}/claims/${id}/${approve ? 'approve' : 'decline'}`), onSuccess: reload, onError: onErr });
  const revoke = useMutation({ mutationFn: (id: string) => api('DELETE', `/trees/${treeId}/invites/${id}`), onSuccess: reload, onError: onErr });
  const rename = useMutation({ mutationFn: () => api('PATCH', `/trees/${treeId}`, { name: name.trim() }), onSuccess: () => { toast(tm.renamed); reload(); }, onError: onErr });
  const del = useMutation({ mutationFn: () => api('DELETE', `/trees/${treeId}`), onSuccess: () => void nav({ to: '/trees' }), onError: onErr });

  if (!snap) return <div className="page"><p className="muted">{t.common.loading}</p></div>;
  const people = snap.people;
  const personName = (id: string | null) => (id ? displayName(people.find((p) => p.id === id) ?? { firstName: '?', lastName: null }) : '—');
  const me = members.data?.members.find((m) => m.userId === myId);

  return (
    <div className="page">
      <div className="row" style={{ marginBottom: 16 }}>
        <BackLink fallback={{ to: '/t/$treeId', params: { treeId } }} aria-label={t.common.backToTree}>←</BackLink>
        <h1 className="grow">{tm.title}</h1>
        {role !== 'viewer' && <button className="btn btn-primary btn-sm" type="button" onClick={() => setInviting(true)}>{tm.inviteFamily}</button>}
      </div>
      <div className="stack">
        {members.data?.claims.map((c) => (
          <div key={c.id} className="banner row">
            <span className="grow">{((parts) => <><strong>{parts[0]}</strong>{parts[1]}<strong>{parts[2]}</strong></>)(tm.claims(c.displayName ?? c.email ?? t.common.someone, personName(c.personId)))}</span>
            <button className="btn btn-sm btn-primary" onClick={() => resolve.mutate({ id: c.id, approve: true })}>{tm.approve}</button>
            <button className="btn btn-sm" onClick={() => resolve.mutate({ id: c.id, approve: false })}>{tm.decline}</button>
          </div>
        ))}
        {members.data?.members.map((m) => (
          <div key={m.userId} className="card-panel stack" style={{ padding: 14 }}>
            <div className="row">
              <div className="grow">
                <strong>{m.displayName ?? m.email ?? tm.member}</strong>
                <div className="muted" style={{ fontSize: 13 }}>{tm.linkedTo(personName(m.personId))}</div>
              </div>
              {isOwner && m.role !== 'owner' ? (
                <select className="input" style={{ width: 130 }} value={m.role} aria-label={tm.roleOf(m.displayName ?? m.email ?? '')} onChange={(e) => {
                  if (e.target.value === 'owner') {
                    if (window.confirm(tm.confirmTransfer)) patch.mutate({ id: m.userId, body: { role: 'owner' } });
                  } else patch.mutate({ id: m.userId, body: { role: e.target.value } });
                }}>
                  <option value="viewer">{t.invite.viewer}</option>
                  <option value="editor">{t.invite.editor}</option>
                  <option value="owner">{tm.makeOwner}</option>
                </select>
              ) : (
                <span className="pill">{t.common.roles[m.role] ?? m.role}</span>
              )}
            </div>
            {isOwner && (
              <div className="row">
                <select className="input grow" value={m.personId ?? ''} aria-label={tm.linkedPerson} onChange={(e) => patch.mutate({ id: m.userId, body: { personId: e.target.value || null } })}>
                  <option value="">{tm.notLinked}</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{displayName(p)}</option>)}
                </select>
                {m.role !== 'owner' && <button className="btn btn-sm btn-danger" onClick={() => window.confirm(tm.confirmRemove) && kick.mutate(m.userId)}>{tm.remove}</button>}
              </div>
            )}
          </div>
        ))}
        {!isOwner && me && (
          <button className="btn btn-danger" onClick={() => window.confirm(tm.confirmLeave) && kick.mutate(me.userId)}>{tm.leave}</button>
        )}

        {isOwner && (
          <>
            <h2 style={{ marginTop: 16 }}>{tm.activeInvites}</h2>
            {invites.data?.length === 0 && <p className="muted">{tm.noInvites}</p>}
            {invites.data?.map((i) => (
              <div key={i.id} className="list-item" style={{ cursor: 'default' }}>
                <div className="grow">
                  <strong>{personName(i.personId)}</strong> <span className="pill">{t.common.roles[i.role] ?? i.role}</span>
                  <div className="muted" style={{ fontSize: 13 }}>{i.email ? tm.onlyEmail(i.email) : tm.anyone} · {tm.used(i.uses, i.maxUses)} · {tm.expires(formatDay(i.expiresAt, lang))}</div>
                </div>
                <button className="btn btn-sm btn-danger" onClick={() => revoke.mutate(i.id)}>{tm.revoke}</button>
              </div>
            ))}

            <h2 style={{ marginTop: 16 }}>{tm.treeSettings}</h2>
            <form className="row" onSubmit={(e) => { e.preventDefault(); if (name.trim()) rename.mutate(); }}>
              <input className="input grow" placeholder={snap.tree.name} value={name} onChange={(e) => setName(e.target.value)} maxLength={80} aria-label={tm.treeName} />
              <button className="btn" disabled={!name.trim()}>{tm.rename}</button>
            </form>
          </>
        )}
        {role !== 'viewer' && <ImportExport treeId={treeId} treeName={snap.tree.name} isOwner={isOwner} />}
        {isOwner && (
          <button className="btn btn-danger" onClick={() => window.prompt(tm.confirmDelete(snap.tree.name)) === snap.tree.name && del.mutate()}>{tm.deleteTree}</button>
        )}
      </div>
      {inviting && <InviteDialog treeId={treeId} treeName={snap.tree.name} person={null} isOwner={isOwner} onClose={() => { setInviting(false); reload(); }} />}
    </div>
  );
}
